/**
 * Mobile auto-hide-on-scroll for the sticky header.
 *
 * Behaviour mirrors the desktop sticky pattern shipped with the theme (see `StoreHeader`
 * in `assets/main.js`): always show near the top of the page, hide while scrolling
 * down, and reveal again on a small upward scroll. The slide animation timing matches
 * `assets/sticky-atc-panel.css` so motion stays consistent.
 *
 * The class toggle pairs with `assets/m-header.css`. Activated by adding
 * `data-mobile-hide-on-scroll="true"` to <store-header>.
 *
 * Desktop: no scroll listener is registered when the viewport is >=769px, and
 * `m-header-hidden` is always cleared so the shipped sticky header behaviour is untouched.
 */

(() => {
  // Match the desktop pattern in assets/main.js -> StoreHeader.handleScroll
  const SCROLL_HIDE_THRESHOLD = 200;
  const SCROLL_REVEAL_THRESHOLD = 5;
  const HIDDEN_CLASS = 'm-header-hidden';

  // Must align with `assets/m-header.css` — desktop must never run this logic or keep
  // `m-header-hidden` (avoids relying on `theme.mediaMatches` before it is ready).
  const MQ_MOBILE = '(max-width: 768.98px)';
  const mqMobile = window.matchMedia(MQ_MOBILE);
  const scrollOpts = { passive: true };

  const isMobileViewport = () => mqMobile.matches;

  // Avoid toggling the class while the page scroll is locked (e.g. mobile menu open).
  const isScrollLocked = () => {
    const { classList } = document.body;
    return classList.contains('fixed') || classList.contains('overflow-hidden');
  };

  const init = () => {
    const header = document.querySelector('store-header[data-mobile-hide-on-scroll="true"]');
    if (!header) return;

    // Run after the main menu open handler (`MainMenu` adds `is-open` in a microtask) so
    // `--header-end` matches the final layout. Pairs with `m-header.css` (no stray fixed CB).
    document.addEventListener('click', (e) => {
      if (!e.target.closest('.main-menu__toggle')) return;
      if (typeof header.setHeaderEnd !== 'function') return;
      setTimeout(() => {
        requestAnimationFrame(() => header.setHeaderEnd());
      }, 0);
    });

    let lastScrollY = window.scrollY;
    let ticking = false;
    let scrollListener = null;

    const update = () => {
      ticking = false;

      if (!isMobileViewport()) {
        header.classList.remove(HIDDEN_CLASS);
        lastScrollY = window.scrollY;
        return;
      }

      if (isScrollLocked()) {
        header.classList.remove(HIDDEN_CLASS);
        lastScrollY = window.scrollY;
        return;
      }

      const currentScrollY = window.scrollY;

      if (currentScrollY <= SCROLL_HIDE_THRESHOLD) {
        header.classList.remove(HIDDEN_CLASS);
      } else if (currentScrollY > lastScrollY) {
        header.classList.add(HIDDEN_CLASS);
      } else if (lastScrollY - currentScrollY > SCROLL_REVEAL_THRESHOLD) {
        header.classList.remove(HIDDEN_CLASS);
      }

      lastScrollY = currentScrollY;
    };

    const onScroll = () => {
      if (ticking) return;
      window.requestAnimationFrame(update);
      ticking = true;
    };

    const syncListeners = () => {
      if (!isMobileViewport()) {
        header.classList.remove(HIDDEN_CLASS);
        if (scrollListener) {
          window.removeEventListener('scroll', scrollListener, scrollOpts);
          scrollListener = null;
        }
        return;
      }

      if (scrollListener) return;

      scrollListener = onScroll;
      window.addEventListener('scroll', scrollListener, scrollOpts);
      update();
    };

    syncListeners();

    const onViewportChange = () => {
      syncListeners();
    };

    window.addEventListener('on:breakpoint-change', onViewportChange);

    if (typeof mqMobile.addEventListener === 'function') {
      mqMobile.addEventListener('change', onViewportChange);
    } else {
      mqMobile.addListener(onViewportChange);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
